import { useBooleanState } from "@gettactic/helpers";
import { getApiErrorMessage } from "@gettactic/helpers/src/errors/getApiErrorMessage";
import React from "react";
import { Modal } from "../Modal";
import { ConfirmModalProps } from "./ConfirmModal.types";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { MobileActionSheet } from "@cmp-src/mobile/MobileActionSheet/MobileActionSheet";
import { Button } from "@cmp-src/primitives/button";

/**
 * Asks before an action: a title, a message and Cancel / Confirm (plus an optional extra
 * action). On phones it is a `MobileActionSheet`.
 */
export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  message,
  onOpenChange,
  onConfirm,
  onCancel,
  onExtra,
  confirmProps,
  cancelProps,
  extraProps,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  extraLabel = "",
  footerProps,
  isOpen,
  children,
  size = "sm",
  buttonComponent,
  ...rest
}) => {
  const FooterButton = buttonComponent || Button;
  const isLoading = useBooleanState(false);
  const [asyncError, setAsyncError] = React.useState("");
  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const handleConfirm = async () => {
    setAsyncError("");

    if (onConfirm) {
      isLoading.true();
      try {
        const results = await onConfirm();
        if (results) {
          const error = getApiErrorMessage(results);
          setAsyncError(error);
        }
      } finally {
        isLoading.false();
      }
    }
  };

  const handleClose = () => {
    if (isLoading.state) return;
    onOpenChange?.(false);
  };

  const handleExtra = async () => {
    setAsyncError("");

    if (onExtra) {
      isLoading.true();
      try {
        const results = await onExtra();
        if (results) {
          const error = getApiErrorMessage(results);
          setAsyncError(error);
        }
      } finally {
        isLoading.false();
      }
    }
  };

  const handleCancel = () => {
    if (isLoading.state) return;

    if (onCancel) {
      onCancel();
    }

    handleClose();
  };

  if (!isMd) {
    // Mirrors the desktop render below: the base message stays, with the
    // error appended under it rather than replacing it.
    const mobileMessage = asyncError ? (
      <>
        {message}
        <span className="mt-1 block text-destructive">{asyncError}</span>
      </>
    ) : (
      message
    );

    return (
      <MobileActionSheet
        isOpen={isOpen}
        title={rest.title || undefined}
        message={mobileMessage || undefined}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
        actions={[
          {
            onSelect: handleConfirm,
            label: confirmLabel,
            // MobileActionSheet reads its own `disabled` field, not
            // `buttonProps.disabled` — confirmProps has to be read out
            // explicitly or a caller-supplied guard (e.g. "still loading,
            // don't let this be confirmed yet") is silently dropped on
            // mobile while it still works on desktop.
            disabled: (confirmProps as { disabled?: boolean } | undefined)
              ?.disabled,
            buttonProps: { variant: "default" }
          },
          {
            onSelect: handleCancel,
            label: cancelLabel
          },
          onExtra && extraLabel
            ? {
                onSelect: onExtra,
                label: extraLabel
              }
            : undefined
        ]}
      />
    );
  }

  const footerButtons = (
    <>
      <FooterButton
        onClick={handleCancel}
        className="w-full sm:w-auto"
        variant="outline"
        disabled={isLoading.state}
        {...(cancelProps as object)}
      >
        {cancelLabel}
      </FooterButton>
      <FooterButton
        onClick={handleConfirm}
        className="w-full sm:ml-3 sm:w-auto"
        variant="default"
        type="submit"
        isLoading={isLoading.state}
        {...(confirmProps as object)}
      >
        {confirmLabel}
      </FooterButton>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      {...rest}
      closeOnOverlayClick={isLoading.state ? false : rest.closeOnOverlayClick}
      onOpenChange={(isOpen) => !isOpen && handleClose()}
      onClose={() => null}
      size={size}
    >
      {message}
      {children}
      {asyncError && (
        <p className="mt-2 text-destructive text-sm">{asyncError}</p>
      )}
      <Modal.Footer variant={rest.variant} {...footerProps}>
        <div className={"flex w-full justify-between gap-2"}>
          {onExtra && extraLabel && (
            <FooterButton
              onClick={handleExtra}
              className="w-full sm:ml-3 sm:w-auto text-primary"
              variant="secondary"
              type="submit"
              isLoading={isLoading.state}
              {...(extraProps as object)}
            >
              {extraLabel}
            </FooterButton>
          )}
          {onExtra && extraLabel ? <div>{footerButtons}</div> : footerButtons}
        </div>
      </Modal.Footer>
    </Modal>
  );
};
